import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async thunks for admin operations
export const fetchAllUsers = createAsyncThunk(
  'admin/fetchAllUsers',
  async (_, { rejectWithValue }) => {
    // This will be called from the component with proper auth token
    // The actual API call is handled in the component
    return [];
  }
);

export const fetchAllOrganizations = createAsyncThunk(
  'admin/fetchAllOrganizations',
  async (_, { rejectWithValue }) => {
    return [];
  }
);

export const fetchAllRoles = createAsyncThunk(
  'admin/fetchAllRoles',
  async (_, { rejectWithValue }) => {
    return [];
  }
);

export const inviteUser = createAsyncThunk(
  'admin/inviteUser',
  async (userData, { rejectWithValue }) => {
    try {
      return userData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserRole = createAsyncThunk(
  'admin/updateUserRole',
  async ({ userUid, organizationId, newRole }, { rejectWithValue }) => {
    try {
      return { userUid, organizationId, newRole };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const removeUserFromOrganization = createAsyncThunk(
  'admin/removeUserFromOrganization',
  async ({ userUid, organizationId }, { rejectWithValue }) => {
    try {
      return { userUid, organizationId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  users: [],
  organizations: [],
  roles: [],
  loading: false,
  error: null,
  success: null,
  isAdminPanelOpen: false,
  isUsersViewOpen: false,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setUsers: (state, action) => {
      state.users = action.payload;
    },
    setOrganizations: (state, action) => {
      state.organizations = action.payload;
    },
    setRoles: (state, action) => {
      state.roles = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    clearMessages: (state) => {
      state.error = null;
      state.success = null;
    },
    openAdminPanel: (state) => {
      state.isAdminPanelOpen = true;
    },
    closeAdminPanel: (state) => {
      state.isAdminPanelOpen = false;
    },
    openUsersView: (state) => {
      state.isUsersViewOpen = true;
    },
    closeUsersView: (state) => {
      state.isUsersViewOpen = false;
    },
    updateUserInList: (state, action) => {
      const { userUid, organizationId, newRole } = action.payload;
      const userIndex = state.users.findIndex(user => user.uid === userUid);
      if (userIndex !== -1) {
        const roleIndex = state.users[userIndex].roles.findIndex(
          role => role.organizationId === organizationId
        );
        if (roleIndex !== -1) {
          state.users[userIndex].roles[roleIndex].role = newRole;
        }
      }
    },
    removeUserFromList: (state, action) => {
      const { userUid, organizationId } = action.payload;
      const userIndex = state.users.findIndex(user => user.uid === userUid);
      if (userIndex !== -1) {
        state.users[userIndex].roles = state.users[userIndex].roles.filter(
          role => role.organizationId !== organizationId
        );
      }
    },
    addUserToList: (state, action) => {
      state.users.push(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all users
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch all organizations
      .addCase(fetchAllOrganizations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllOrganizations.fulfilled, (state, action) => {
        state.loading = false;
        state.organizations = action.payload;
      })
      .addCase(fetchAllOrganizations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch all roles
      .addCase(fetchAllRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = action.payload;
      })
      .addCase(fetchAllRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Invite user
      .addCase(inviteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(inviteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'User invited successfully!';
      })
      .addCase(inviteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update user role
      .addCase(updateUserRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'User role updated successfully!';
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Remove user from organization
      .addCase(removeUserFromOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeUserFromOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'User removed successfully!';
      })
      .addCase(removeUserFromOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setUsers,
  setOrganizations,
  setRoles,
  setLoading,
  setError,
  setSuccess,
  clearMessages,
  openAdminPanel,
  closeAdminPanel,
  openUsersView,
  closeUsersView,
  updateUserInList,
  removeUserFromList,
  addUserToList,
} = adminSlice.actions;

export default adminSlice.reducer;
